import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goodsReceiptService } from '../../services/goodsReceiptService';
import { GoodsReceipt, GoodsReceiptFilters, GOODS_RECEIPT_STATUS_LABELS } from '../../types/goodsReceipt';
import { formatDate } from '../../utils/formatters';
import { generateGoodsReceiptPDF } from '../../utils/goodsReceiptPdfGenerator';
import { companyService } from '../../services/companyService';
import { invoiceTemplateService } from '../../services/invoiceTemplateService';

interface GoodsReceiptListProps {
  filters: GoodsReceiptFilters;
  onViewGRN: (grn: GoodsReceipt) => void;
  expandedGRNId: string | null;
  setExpandedGRNId: (id: string | null) => void;
}

const grnStatusStyle: Record<string, React.CSSProperties> = {
  DRAFT:     { background: 'rgba(120,120,140,0.15)', color: '#9a9ab0', border: '1px solid rgba(120,120,140,0.25)' },
  POSTED:    { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  CANCELLED: { background: 'rgba(80,80,80,0.15)', color: '#555570', border: '1px solid rgba(80,80,80,0.2)' },
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

const subThStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 8,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const subTdStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 11,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const GoodsReceiptList: React.FC<GoodsReceiptListProps> = ({
  filters,
  onViewGRN,
  expandedGRNId,
  setExpandedGRNId: _setExpandedGRNId
}) => {
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<{ type: 'post' | 'delete' | 'cancel'; grn: GoodsReceipt } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['goods-receipts', filters],
    queryFn: () => goodsReceiptService.getGoodsReceipts(filters)
  });

  const postMutation = useMutation({
    mutationFn: (id: string) => goodsReceiptService.postGoodsReceipt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-stock'] });
      setConfirmAction(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => goodsReceiptService.deleteGoodsReceipt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-receipts'] });
      setConfirmAction(null);
    }
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => goodsReceiptService.cancelGoodsReceipt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['warehouse-stock'] });
      setConfirmAction(null);
    }
  });

  const { data: company } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => companyService.getProfile()
  });

  const { data: templates } = useQuery({
    queryKey: ['invoice-templates'],
    queryFn: () => invoiceTemplateService.getAll()
  });

  const defaultTemplate = templates?.find((t) => t.isDefault) || templates?.[0];

  const handleExportPDF = (grn: GoodsReceipt) => {
    const addressParts = [
      company?.address,
      company?.city,
      company?.state,
      company?.country
    ].filter(Boolean);

    generateGoodsReceiptPDF({
      goodsReceipt: grn,
      companyName: company?.name || '',
      companyAddress: addressParts.join(', '),
      companyPhone: company?.phone || '',
      companyEmail: company?.email || '',
      template: grn.template || defaultTemplate
    });
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Goods Receipts</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 36, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    console.error('Goods receipts loading error:', error);
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Error loading goods receipts. Please try again.</p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 6 }}>{(error as Error).message || 'Unknown error'}</p>
      </div>
    );
  }

  if (!data?.goodsReceipts.length) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
        <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO GOODS RECEIPTS FOUND</p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 6 }}>Get started by creating your first goods receipt.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Goods Receipts</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            {data.pagination?.totalItems ?? 0} total receipts
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
              <th style={thStyle}>GRN Number</th>
              <th style={thStyle}>PO Number</th>
              <th style={thStyle}>Warehouse</th>
              <th style={thStyle}>Received Date</th>
              <th style={thStyle}>Received By</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.goodsReceipts.map((grn: GoodsReceipt) => (
              <React.Fragment key={grn.id}>
                <tr
                  style={{
                    transition: 'background 0.15s',
                    cursor: 'pointer',
                    background: expandedGRNId === grn.id ? 'rgba(239,232,213,0.5)' : 'transparent',
                  }}
                  onMouseEnter={e => { if (expandedGRNId !== grn.id) e.currentTarget.style.background = 'rgba(248,250,252,1)'; }}
                  onMouseLeave={e => { if (expandedGRNId !== grn.id) e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => onViewGRN(grn)}
                >
                  <td style={tdStyle}>
                    <div style={{ color: '#5ac98c', fontWeight: 500 }}>{grn.receiptNumber}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ color: '#d97706' }}>{grn.purchaseOrder?.poNumber || 'N/A'}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{grn.warehouse?.name || 'N/A'}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{formatDate(grn.receivedDate)}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{grn.receivedBy}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      ...(grnStatusStyle[grn.status] || grnStatusStyle.DRAFT),
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 3,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                    }}>
                      {GOODS_RECEIPT_STATUS_LABELS[grn.status] || grn.status}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); onViewGRN(grn); }}
                      style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      View
                    </button>
                  </td>
                </tr>

                {expandedGRNId === grn.id && grn.items && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0, borderBottom: '1px solid rgba(241,245,249,1)' }}>
                      <div style={{ background: '#faf7ee', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: '0.2em', color: '#d97706', textTransform: 'uppercase' }}>
                          Goods Receipt Items
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                              <th style={{ ...subThStyle, textAlign: 'left' }}>Product</th>
                              <th style={{ ...subThStyle, textAlign: 'left' }}>PO Item</th>
                              <th style={{ ...subThStyle, textAlign: 'right' }}>Ordered</th>
                              <th style={{ ...subThStyle, textAlign: 'right' }}>Received</th>
                              <th style={{ ...subThStyle, textAlign: 'right' }}>Rejected</th>
                              <th style={{ ...subThStyle, textAlign: 'left' }}>Serial Numbers</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grn.items?.map((item, index) => (
                              <tr
                                key={index}
                                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(201,165,90,0.02)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                              >
                                <td style={{ ...subTdStyle, color: '#1c2033' }}>{item.product?.name || 'N/A'}</td>
                                <td style={{ ...subTdStyle, color: '#6b6a5c' }}>{item.poItem?.description || '—'}</td>
                                <td style={{ ...subTdStyle, textAlign: 'right' }}>{item.poItem?.quantity || 0}</td>
                                <td style={{ ...subTdStyle, textAlign: 'right' }}>
                                  <span style={{ fontWeight: 500, color: '#5ac98c' }}>{item.quantityReceived}</span>
                                </td>
                                <td style={{ ...subTdStyle, textAlign: 'right' }}>
                                  {item.quantityRejected > 0 ? (
                                    <span style={{ fontWeight: 500, color: '#c95a5a' }}>{item.quantityRejected}</span>
                                  ) : (
                                    <span style={{ color: '#8a8778' }}>0</span>
                                  )}
                                </td>
                                <td style={{ ...subTdStyle, color: '#6b6a5c' }}>{item.serialNumbers || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {grn.notes && (
                          <div style={{ paddingTop: 12, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', color: '#6b6a5c', textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>{grn.notes}</div>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{ paddingTop: 12, borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', gap: 20 }}>
                            {grn.status === 'DRAFT' && (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'post', grn }); }}
                                  style={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: 11,
                                    color: '#5a8cc9',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                  }}
                                >
                                  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Post (Update Stock)
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'delete', grn }); }}
                                  style={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: 11,
                                    color: '#c95a5a',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                  }}
                                >
                                  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete Draft
                                </button>
                              </>
                            )}
                            {grn.status === 'POSTED' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: 'cancel', grn }); }}
                                style={{
                                  fontFamily: "'Inter', sans-serif",
                                  fontSize: 11,
                                  color: '#c95a5a',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancel Receipt
                              </button>
                            )}
                          </div>

                          <button
                            onClick={() => handleExportPDF(grn)}
                            style={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 11,
                              color: '#5ac98c',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export to PDF
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(7,7,15,0.75)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#ffffff',
            border: '1px solid rgba(92,78,51,0.25)',
            borderRadius: 6,
            padding: 28,
            width: '100%',
            maxWidth: 440,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033', marginBottom: 8 }}>
              {confirmAction.type === 'post' && 'Post Goods Receipt'}
              {confirmAction.type === 'delete' && 'Delete Goods Receipt'}
              {confirmAction.type === 'cancel' && 'Cancel Goods Receipt'}
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#d97706', marginBottom: 4 }}>
              {confirmAction.grn.receiptNumber}
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', marginBottom: 20, lineHeight: 1.6 }}>
              {confirmAction.type === 'post' && 'This will update inventory stock levels and mark the receipt as posted. This action cannot be undone directly.'}
              {confirmAction.type === 'delete' && 'This will permanently delete this draft goods receipt. This action cannot be undone.'}
              {confirmAction.type === 'cancel' && 'This will reverse all stock movements and inventory updates from this receipt.'}
            </div>

            {(postMutation.isError || deleteMutation.isError || cancelMutation.isError) && (
              <div style={{
                marginBottom: 16,
                background: 'rgba(201,90,90,0.08)',
                border: '1px solid rgba(201,90,90,0.2)',
                borderRadius: 4,
                padding: '8px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                color: '#c95a5a',
              }}>
                {(postMutation.error as any)?.response?.data?.message ||
                 (deleteMutation.error as any)?.response?.data?.message ||
                 (cancelMutation.error as any)?.response?.data?.message ||
                 'An error occurred. Please try again.'}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
              <button
                onClick={() => setConfirmAction(null)}
                disabled={postMutation.isPending || deleteMutation.isPending || cancelMutation.isPending}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11,
                  color: '#6b6a5c',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  opacity: (postMutation.isPending || deleteMutation.isPending || cancelMutation.isPending) ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmAction.type === 'post') postMutation.mutate(confirmAction.grn.id);
                  if (confirmAction.type === 'delete') deleteMutation.mutate(confirmAction.grn.id);
                  if (confirmAction.type === 'cancel') cancelMutation.mutate(confirmAction.grn.id);
                }}
                disabled={postMutation.isPending || deleteMutation.isPending || cancelMutation.isPending}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11,
                  color: confirmAction.type === 'post' ? '#5a8cc9' : '#c95a5a',
                  background: 'none',
                  border: 'none',
                  cursor: (postMutation.isPending || deleteMutation.isPending || cancelMutation.isPending) ? 'not-allowed' : 'pointer',
                  padding: 0,
                  opacity: (postMutation.isPending || deleteMutation.isPending || cancelMutation.isPending) ? 0.5 : 1,
                  fontWeight: 500,
                }}
              >
                {(postMutation.isPending || deleteMutation.isPending || cancelMutation.isPending)
                  ? 'Processing...'
                  : confirmAction.type === 'post'
                    ? 'Yes, Post Receipt'
                    : confirmAction.type === 'delete'
                      ? 'Yes, Delete'
                      : 'Yes, Cancel Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {data.pagination && data.pagination.totalPages > 1 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
            Showing {((data.pagination.currentPage - 1) * 10) + 1}–{Math.min(data.pagination.currentPage * 10, data.pagination.totalItems)} of {data.pagination.totalItems}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={data.pagination.currentPage === 1}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: data.pagination.currentPage === 1 ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: data.pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: data.pagination.currentPage === 1 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <button
              disabled={data.pagination.currentPage === data.pagination.totalPages}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: data.pagination.currentPage === data.pagination.totalPages ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: data.pagination.currentPage === data.pagination.totalPages ? 'not-allowed' : 'pointer',
                opacity: data.pagination.currentPage === data.pagination.totalPages ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
